import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { sxStyles } from "./SelfHelpToolsPage.styles";
import BannerCard from "lib/components/cards/BannerCard/BannerCard";
import {
  mobileNavigate,
  navigateToExternalInMobileBrowser,
  navigateToExternalInMobileBrowserForStandardPage,
} from "lib/routing/navigate/navigate";
import IMAGES from "lib/assets/images";
import CompactCard from "lib/components/cards/CompactCard/CompactCard";
import { logEventToGoogleAnalytics } from "lib/util/GoogleAnalyticsUtil/logEvent";
import { EVENTS } from "lib/util/GoogleAnalyticsUtil/events";
import { NUHS_POSTALCODE } from "ui/mhm/components/Dashboard/Constants";
import Page from "lib/components/layout/Page/Page";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import { enqueueMessage } from "lib/redux/navigation/navigationSlice";
import { MessageActions } from "lib/routing/messageChannel/messageActions";
import { screenForLifeBackUrl } from "lib/configs";
import { ACTIONS } from "lib/routing";
import { useEffect } from "react";
import { fetchMyChartSystemSettings } from "ui/medrefill/ducks/thunks";
import { selectMhm, selectMhmSystemSettings } from "ui/mhm/ducks/selectors";
import ErrorDisplay from "lib/components/error/ErrorDisplay/ErrorDisplay";

// Constants
const BODY_TITLE = "Navigate to Better Health";
const CARDS = {
  screening_vaccination: { title: "Screening and Vaccination", body: "" },
  sfl: { title: "Check your eligibility for Healthier SG Screening", body: "" },
  dos_clinic: { title: "Find a screening clinic near you", body: "" },
  dos_dining: { title: "Find healthier eating options near you", body: "" },
  champ: {
    title: "CHAMP",
    body: "Better manage your blood pressure and more with our WhatsApp chatbot",
  },
};

const CHAMPS_URL = "https://for.sg/enrol-nuhs-champ";
const BUTTON_TEXT = "View other tools ";
const BUTTON_URL =
  "https://www.nuhs.edu.sg/Care-in-the-Community/Enabling-Community-Care/Health-Together/self-help-tools/Pages/default.aspx";
const CLINIC_CATEGORY_CODE = "PR_CDM";

const SelfHelpToolsPage = () => {
  const theme = useTheme();
  const classes = sxStyles(theme);
  const dispatch = useAppDispatch();

  // Redux states
  const {
    isLoading: isLoadingSystemSettings,
    hasErrored: hasErroredSystemSettings,
    errorMessage: errorMessageSystemSettings,
  } = useAppSelector(selectMhmSystemSettings);
  const showPreventiveCare =
    useAppSelector(selectMhm).showMyChartPreventiveCare;

  const handleViewOtherToolsOnClick = () => {
    navigateToExternalInMobileBrowser(BUTTON_URL);
  };

  const handleCHAMPSOnClick = () => {
    logEventToGoogleAnalytics(EVENTS.CLICK_SELF_SERVE_CHAMP);
    navigateToExternalInMobileBrowser(CHAMPS_URL);
  };

  const handleDOSClinicOnClick = () => {
    logEventToGoogleAnalytics(EVENTS.CLICK_SCREENING_CLINIC_NEAR_YOU);

    dispatch(
      enqueueMessage(
        MessageActions.dosClinic({
          postalCode: NUHS_POSTALCODE,
          serviceTypes: [CLINIC_CATEGORY_CODE],
        }),
      ),
    );
  };

  const handleDOSDiningOnClick = () => {
    logEventToGoogleAnalytics(EVENTS.SELECT_HEALTHIER_DINING_OPTIONS);

    dispatch(
      enqueueMessage(
        MessageActions.dosDining({
          postalCode: NUHS_POSTALCODE,
        }),
      ),
    );
  };

  const handleSFLOnClick = () => {
    navigateToExternalInMobileBrowserForStandardPage(
      screenForLifeBackUrl(),
      "hh",
    );
  };

  const handleScreeningOnClick = () => {
    logEventToGoogleAnalytics(EVENTS.SELECT_MHM_PREVENTIVE_CARE);
    mobileNavigate(ACTIONS.MY_CHART.PREVENTIVE_CARE);
  };

  useEffect(() => {
    if (hasErroredSystemSettings === null) {
      dispatch(fetchMyChartSystemSettings());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErroredSystemSettings]);

  return (
    <Page>
      {isLoadingSystemSettings ? (
        <Box display="flex" align-items="center" justifyContent="center" pt={4}>
          <CircularProgress />
        </Box>
      ) : hasErroredSystemSettings ? (
        <ErrorDisplay
          errorMessage={errorMessageSystemSettings}
          onTryAgain={() => {
            dispatch(fetchMyChartSystemSettings());
          }}
        />
      ) : (
        <Box sx={classes.mainContainer}>
          <Box sx={classes.topContainer}>
            <Box sx={classes.bannerContainer}>
              <BannerCard
                bannerImage={IMAGES.healthTogetherMHM.OtherToolsImage}
              />
            </Box>
            <Box sx={classes.bodyContainer}>
              <Typography sx={classes.bodyTitle}>{BODY_TITLE}</Typography>
              {showPreventiveCare && (
                <CompactCard
                  title={CARDS.screening_vaccination.title}
                  titleIcon={IMAGES.healthTogetherMHM.ScreeningAndVaccination}
                  onClickCard={handleScreeningOnClick}
                />
              )}

              <CompactCard
                title={CARDS.sfl.title}
                titleIcon={IMAGES.healthTogetherMHM.HSG_Logo}
                onClickCard={handleSFLOnClick}
              />
              <CompactCard
                title={CARDS.dos_clinic.title}
                titleIcon={IMAGES.healthTogetherMHM.FindADoctorIcon}
                onClickCard={handleDOSClinicOnClick}
              />
              <CompactCard
                title={CARDS.dos_dining.title}
                titleIcon={IMAGES.healthTogetherMHM.HDO_Logo}
                onClickCard={handleDOSDiningOnClick}
              />
              <CompactCard
                title={CARDS.champ.title}
                body={CARDS.champ.body}
                onClickCard={handleCHAMPSOnClick}
                titleIcon={IMAGES.healthTogetherMHM.CHAMPIcon}
              />
            </Box>
          </Box>
          <Box sx={classes.footerContainer}>
            <Button
              onClick={handleViewOtherToolsOnClick}
              variant="outlined"
              fullWidth
            >
              {BUTTON_TEXT}
            </Button>
          </Box>
        </Box>
      )}
    </Page>
  );
};

export default SelfHelpToolsPage;
